import { createSelector, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { APIService } from 'src/api/api';
import { DATE_FORMAT } from 'src/utils/constants';
import { updateItemInArray } from 'src/utils/getInitials';

const initialState = {
  shiftDetails: [],
  appSettings: null,
  appSettingsLoaded: false,
  appSettingsLoading: false,
  shiftLoaded: false,
  shiftLoading: false,
  configuration: {},
  loading: false,
};

export const getAppSettings = createAsyncThunk(
  'settings/app/settings', async (_, { rejectWithValue }) => {
    try {
      const res = await APIService.getSettings();
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
  {
    condition: (_, { getState }) => {
      const { appSettingsLoaded, appSettingsLoading } = getState().settings;
      return !appSettingsLoaded && !appSettingsLoading;
    }
  }
);

export const getShiftDetails = createAsyncThunk(
  'settings/shift/details', async (_, { rejectWithValue }) => {
    try {
      const res = await APIService.getShiftDetails();
      if (!res.success) throw res;
      return res.result.results;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
  {
    condition: (_, { getState }) => {
      const { shiftLoaded, shiftLoading } = getState().settings;
      return !shiftLoaded && !shiftLoading;
    }
  }
);

export const updateShiftDetails = createAsyncThunk(
  'settings/update/shift', async (data, { rejectWithValue }) => {
    try {
      const res = await APIService.updateShift(data.id, data);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const addShiftDetails = createAsyncThunk(
  'settings/create/shift', async (data, { rejectWithValue },) => {
    try {
      const res = await APIService.createShift(data);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const settingSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // add your async reducers here
    builder
      .addCase(getAppSettings.pending, (state) => ({
        ...state, appSettingsLoading: true
      }))
      .addCase(getAppSettings.fulfilled, (state, action) => ({
        ...state, appSettings: action.payload, appSettingsLoaded: true, appSettingsLoading: false
      }))
      .addCase(getAppSettings.rejected, (state) => ({
        ...state, appSettingsLoading: false
      }))
      .addCase(getShiftDetails.pending, (state) => ({
        ...state, shiftLoading: true
      }))
      .addCase(getShiftDetails.fulfilled, (state, action) => ({
        ...state, shiftDetails: action.payload, shiftLoaded: true, shiftLoading: false
      }))
      .addCase(getShiftDetails.rejected, (state) => ({
        ...state, shiftLoading: false
      }))
      .addCase(updateShiftDetails.fulfilled, (state, action) => ({
        ...state, loading: false, shiftDetails: updateItemInArray(state.shiftDetails, action.payload)
      }))
      .addCase(updateShiftDetails.pending, (state) => ({ ...state, loading: true }))
      .addCase(updateShiftDetails.rejected, (state) => ({ ...state, loading: false }))
      .addCase(addShiftDetails.pending, (state) => ({ ...state, loading: true }))
      .addCase(addShiftDetails.rejected, (state) => ({ ...state, loading: false }))
      .addCase(addShiftDetails.fulfilled, (state, action) => {
        state.shiftDetails.push(action.payload);
      });
  }
});

export default settingSlice.reducer;

const shiftDetails = (state) => state.settings.shiftDetails;

export const getFormattedShiftDetails = createSelector(
  [shiftDetails],
  (shiftData) => shiftData.map((shift) => {
    const endDateTime = moment(shift.shift_starts_from).add(shift.shift_duration, 'hours');
    return {
      ...shift,
      shift_end_time: endDateTime.format(DATE_FORMAT.unixDateTime)
    };
  })
);
